import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  getUtilityIcon,
  getUtilityTypeReadableName,
} from 'utils/utilityTypeUtils/utilityTypeUtils';
import Tippy from '@tippyjs/react';
import { StatusChip } from '@components/statusChip';
import { numericFormatter } from 'react-number-format';
import { convertToReadableDate } from 'utils/dateUtils/dateUtils';
import { getDaysFromStatusLastUpdate } from 'utils/formatters/getDaysFromDate/getDaysFromDate';
import { useTranslation } from 'react-i18next';

interface AnomaliesTableColumnsReturnType {
  columns: ColumnDef<any, any>[];
}

const useGetOrganizationAnomaliesTableColumns =
  (): AnomaliesTableColumnsReturnType => {
    const columnHelper = createColumnHelper<any>();
    const { t } = useTranslation();

    const columns = [
      columnHelper.accessor('name', {
        header: () =>
          t('organizationView.anomaliesAndProjects.columns.meterName'),
        cell: (info) => {
          const values = info.row.original;
          return (
            <Tippy
              content={
                <div className="p-2">
                  <p>
                    {t(
                      'organizationView.anomaliesAndProjects.columns.meterName'
                    )}
                    : {values.meter_name}
                  </p>
                  <p>
                    {t(
                      'organizationView.anomaliesAndProjects.columns.utilityType'
                    )}
                    : {getUtilityTypeReadableName(values.utility_type)}
                  </p>
                  <p>
                    {t(
                      'organizationView.anomaliesAndProjects.columns.momentOfChange'
                    )}
                    : {convertToReadableDate(values.moment_change)}
                  </p>
                  <p>
                    {t(
                      'organizationView.anomaliesAndProjects.columns.timeOnStatus'
                    )}
                    : {getDaysFromStatusLastUpdate(values.time_on_status)}
                  </p>
                </div>
              }
              maxWidth={450}
              placement="top-start"
            >
              <span className="flex items-center gap-2.5">
                <div className="fill-gray-50">
                  {getUtilityIcon(values.utility_type)}
                </div>
                <span className="max-w-[100px] desktop-l:max-w-[120px] block text-ellipsis overflow-hidden truncate">
                  {values.meter_name ||
                    getUtilityTypeReadableName(values.utility_type)}
                </span>
              </span>
            </Tippy>
          );
        },
        enableSorting: true,
        size: 100,
        maxSize: 120,
      }),
      columnHelper.accessor('asset_name', {
        header: () =>
          t('organizationView.anomaliesAndProjects.columns.building'),
        cell: (info) => {
          return (
            <Tippy content={info.getValue()} maxWidth={450}>
              <span className="flex items-center gap-2.5">
                <span className="max-w-[120px] desktop-l:max-w-[140px] block text-ellipsis overflow-hidden truncate">
                  {info.renderValue()}
                </span>
              </span>
            </Tippy>
          );
        },
        enableSorting: true,
        size: 120,
        maxSize: 140,
      }),
      columnHelper.accessor('status', {
        header: () => t('organizationView.anomaliesAndProjects.columns.status'),
        cell: (info) => <StatusChip status={info.getValue()} />,
        enableSorting: true,
        size: 80,
        maxSize: 120,
      }),
      columnHelper.accessor('impact_average_diff_euro', {
        header: () => (
          <span className="whitespace-nowrap">
            {t('organizationView.anomaliesAndProjects.columns.impactPerDay')}
          </span>
        ),
        cell: (info) =>
          `${numericFormatter((info.getValue() ?? 0).toFixed(2), {
            decimalSeparator: ',',
            thousandSeparator: '.',
            decimalScale: 2,
          })} €`,
        enableSorting: true,
        size: 120,
        maxSize: 140,
      }),
    ];

    return {
      columns,
    };
  };
export default useGetOrganizationAnomaliesTableColumns;
